import React, { useCallback, useMemo, useState, useRef } from "react";
import { Container, Header, SpaceBetween, ExpandableSection, Badge } from "@amzn/awsui-components-react";
import CbeResults from "./CbeResults";
import CbeHealthStatusDisplay from "./CbeHealthStatusDisplay";
import { CanonicalBusinessEvent } from "../../types";
import { DigitalBusinessDocument } from "../../types/documents";
import VersionSelector from "../common/VersionSelector";
import {
  groupCbesByProduct,
  isBundleNonSplitTransaction,
  isBundleTransaction,
} from "../../helpers/bundleUtils";
import { BADGE_COLORS } from "../../helpers/errorUtils";

interface Props {
  cbeData: CanonicalBusinessEvent[];
  onVersionChange: (index: number) => void;
  document?: DigitalBusinessDocument;
  onProductsToggle?: (toggleFunction: () => void) => void;
}

export default function CbeVersionSelector({
  cbeData,
  onVersionChange,
  document,
  onProductsToggle,
}: Props) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [allProductsExpanded, setAllProductsExpanded] = useState(true);

  const { productGroups, isBundle } = useMemo(() => {
    const isBundleDetected = isBundleTransaction(document);
    const isBundleNonSplitDetected = isBundleNonSplitTransaction(document);

    if (!isBundleDetected || isBundleNonSplitDetected) {
      return { productGroups: [], isBundle: false };
    }
    const groups = groupCbesByProduct(cbeData, document);

    // If no groups found, fall back to non-bundle rendering
    if (groups.size === 0) {
      return { productGroups: [], isBundle: false };
    }

    return {
      productGroups: Array.from(groups.entries()).map(([productId, cbes]) => ({
        productId,
        cbes,
      })),
      isBundle: true,
    };
  }, [cbeData, document]);

  const toggleAllProducts = useCallback(() => {
    const newState = !allProductsExpanded;
    setAllProductsExpanded(newState);
    
    const newExpandedGroups = productGroups.reduce((acc, group) => {
      acc[group.productId] = newState;
      return acc;
    }, {} as Record<string, boolean>);
    setExpandedGroups(newExpandedGroups);
  }, [allProductsExpanded, productGroups]);

  // Call onProductsToggle only when we have multiple products
  React.useMemo(() => {
    if (onProductsToggle && isBundle && productGroups.length > 1) {
      onProductsToggle(toggleAllProducts);
    }
  }, [isBundle, productGroups.length, toggleAllProducts]);

  const handleBundleVersionChange = useCallback(
    (groupIndex: number) => (relativeIndex: number) => {
      let absoluteIndex = 0;
      for (let i = 0; i < groupIndex; i += 1) {
        absoluteIndex += productGroups[i].cbes.length;
      }
      onVersionChange(absoluteIndex + relativeIndex);
    },
    [productGroups, onVersionChange]
  );

  const renderTabLabel = useCallback(
    (version: number, cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeHealthStatusDisplay
        name="Version"
        events={cbesForVersion}
        value={version.toString()}
      />
    ),
    []
  );

  const renderTabContent = useCallback(
    (cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeResults cbeData={cbesForVersion} />
    ),
    []
  );

  if (!isBundle) {
    return (
      <VersionSelector
        data={cbeData}
        onVersionChange={onVersionChange}
        getVersion={(cbe) => cbe.lockVersion}
        renderTabLabel={renderTabLabel}
        renderTabContent={renderTabContent}
      />
    );
  }

  return (
    <SpaceBetween size="l">
      {productGroups.map((group, index) => (
        <ExpandableSection
          key={group.productId}
          variant="container"
          expanded={expandedGroups[group.productId] ?? true}
          onChange={(event) =>
            setExpandedGroups(prev => ({
              ...prev,
              [group.productId]: event.detail.expanded
            }))
          }
          header={
            <Header 
              variant="h3"
              description={
                <small>
                  {group.cbes.map(cbe => cbe.usecaseName)
                    .filter((name, index, arr) => name && arr.indexOf(name) === index)
                    .join(", ") || ""}
                </small>
              }
            >
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Badge color={BADGE_COLORS[index % BADGE_COLORS.length]}>{index + 1}</Badge>
                Product: {group.productId}
              </SpaceBetween>
            </Header>
          }
        >
          <VersionSelector
            data={group.cbes}
            onVersionChange={handleBundleVersionChange(index)}
            getVersion={(cbe) => cbe.lockVersion}
            renderTabLabel={renderTabLabel}
            renderTabContent={renderTabContent}
          />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
}
